import type { MetaInfo } from 'streams/views/mainContent/types';
import { useRef, type MouseEvent, type RefObject } from 'react';
import { IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { t, type I18nLocale } from '@ardoq/i18n';
import { ArdoqId } from '@ardoq/api-types';
import {
  closeSlideOverview,
  nextSlide,
  previousSlide,
  toggleSlideOverview,
} from './actions';
import {
  HideNavigatorButton,
  NavigatorDivider,
  NavigatorPresentationName,
  SLIDE_OVERVIEW_PANEL_ID,
  SlidePosition,
  SlideProgress,
} from './atoms';
import { SlideOverview } from './SlideOverview';
import { Slide } from './types';
import { Text } from '@ardoq/typography';
import { tourAnchorIds } from '@ardoq/product-tours';

type SlideNavigatorControlsProps = {
  slides: Slide[];
  presentationName: string;
  selectedSlideId: ArdoqId | null;
  isSlideOverviewOpen: boolean;
  hasDescription: boolean;
  isDescriptionShown: boolean;
  onToggleDescription: VoidFunction;
  onHideNavigator: (event: MouseEvent<HTMLButtonElement>) => void;
  hideNavigatorRef: RefObject<HTMLButtonElement | null>;
  deprecatedViews: MetaInfo[];
  i18nLocale: I18nLocale;
};

export const SlideNavigatorControls = ({
  slides,
  presentationName,
  selectedSlideId,
  isSlideOverviewOpen,
  hasDescription,
  isDescriptionShown,
  onToggleDescription,
  onHideNavigator,
  hideNavigatorRef,
  deprecatedViews,
  i18nLocale,
}: SlideNavigatorControlsProps) => {
  const slideOverviewRef = useRef<HTMLButtonElement>(null);

  const currentSlideIndex = slides.findIndex(
    ({ id }) => id === selectedSlideId
  );
  const hasSelectedSlide = currentSlideIndex >= 0;
  const currentSlideNumber = currentSlideIndex + 1;
  const slideCount = slides.length;
  return (
    <>
      <SlideProgress
        currentSlideNumber={currentSlideNumber}
        slideCount={slideCount}
      />

      <IconButton
        ref={slideOverviewRef}
        data-tour-id={tourAnchorIds.presentationSlideList}
        isActive={isSlideOverviewOpen}
        iconName={IconName.TABLE_ROWS}
        label={t(
          i18nLocale,
          'presentationAdmin:viewer.slideNavigator.slideOverview',
          'Slides'
        )}
        aria-expanded={isSlideOverviewOpen}
        aria-controls={
          isSlideOverviewOpen ? SLIDE_OVERVIEW_PANEL_ID : undefined
        }
        onClick={() => dispatchAction(toggleSlideOverview())}
        dataTestId="presentation-slide-overview"
      />

      {isSlideOverviewOpen && (
        <SlideOverview
          slides={slides}
          selectedSlideId={selectedSlideId}
          anchorRef={slideOverviewRef}
          onClose={() => dispatchAction(closeSlideOverview())}
          deprecatedViews={deprecatedViews}
          i18nLocale={i18nLocale}
        />
      )}

      <FlexBox
        align="center"
        gap="medium"
        flexGrow={1}
        minWidth="0"
        paddingX="small"
      >
        <NavigatorPresentationName>
          <Text variant="text1Bold" color="textDefault" truncate>
            {presentationName}
          </Text>
        </NavigatorPresentationName>
        {hasSelectedSlide && (
          <SlidePosition variant="caption" color="textSubtle">
            {t(
              i18nLocale,
              'presentationAdmin:viewer.slideNavigator.slidePosition',
              { current: currentSlideNumber, total: slideCount },
              'Slide {{current}} of {{total}}'
            )}
          </SlidePosition>
        )}
      </FlexBox>

      {hasDescription && (
        <IconButton
          data-tour-id={tourAnchorIds.presentationSlideDescription}
          isActive={isDescriptionShown}
          iconName={IconName.DESCRIPTION}
          label={
            isDescriptionShown
              ? t(
                  i18nLocale,
                  'presentationAdmin:viewer.slideNavigator.hideDescription',
                  'Hide slide description'
                )
              : t(
                  i18nLocale,
                  'presentationAdmin:viewer.slideNavigator.showDescription',
                  'Show slide description'
                )
          }
          onClick={onToggleDescription}
          dataTestId="presentation-toggle-description"
        />
      )}

      <NavigatorDivider />

      <FlexBox
        align="center"
        gap="xxsmall"
        data-tour-id={tourAnchorIds.presentationSlideNavigation}
      >
        <IconButton
          isDisabled={currentSlideIndex <= 0}
          iconName={IconName.CHEVRON_LEFT}
          label={t(
            i18nLocale,
            'presentationAdmin:viewer.slideNavigator.previousSlide',
            'Previous slide'
          )}
          onClick={() => dispatchAction(previousSlide())}
          dataTestId="presentation-previous-slide"
        />
        <IconButton
          isDisabled={currentSlideIndex === slideCount - 1}
          iconName={IconName.CHEVRON_RIGHT}
          label={t(
            i18nLocale,
            'presentationAdmin:viewer.slideNavigator.nextSlide',
            'Next slide'
          )}
          onClick={() => dispatchAction(nextSlide())}
          dataTestId="presentation-next-slide"
        />
      </FlexBox>

      <NavigatorDivider />

      <HideNavigatorButton
        ref={hideNavigatorRef}
        tourAnchorId={tourAnchorIds.presentationHideNavigator}
        label={t(
          i18nLocale,
          'presentationAdmin:viewer.slideNavigator.hideNavigator',
          'Hide slide navigation'
        )}
        onClick={onHideNavigator}
      />
    </>
  );
};
